/**
 * Analytic two-bone IK (plan D6, D22).
 *
 * Two layers:
 *  - `solveTwoBoneIK` — a pure function on world positions. No three.js objects, fully
 *    deterministic, never returns NaN, clamps unreachable targets to full extension.
 *  - `solveChainOnBones` — applies that solution to live three.js bones by writing local
 *    quaternions only. Positions and scales are never touched, and the end bone's rotation is
 *    left alone (D22: IK targets are position-only; hand/foot orientation stays FK).
 *
 * The bone layer is purely positional: it reads world joint positions and aims each bone at the
 * solved child position with a minimal from-to rotation. It therefore works with any rest local
 * axis convention (canonical identity rest per D4, or Mixamo-style +Y-down-the-limb).
 */

import { Quaternion, Vector3 } from 'three';
import type { Object3D } from 'three';

import type { CanonicalBoneId, IkChain, Vec3Tuple } from '../shared';

/** Lengths below this are treated as zero. */
const EPS_LEN = 1e-9;
/** A bend-direction projection shorter than this is degenerate and triggers the fallback. */
const EPS_PERP = 1e-6;
/** Slack when deciding whether a target was reachable at all. */
const REACH_TOL = 1e-6;

const WORLD_UP: Vec3Tuple = [0, 1, 0];
const WORLD_X: Vec3Tuple = [1, 0, 0];
const WORLD_Z: Vec3Tuple = [0, 0, 1];

export interface TwoBoneIKInput {
  /** World position of the chain root joint (shoulder / hip). Never moved by the solver. */
  root: Vec3Tuple;
  /** World position of the middle joint (elbow / knee). */
  mid: Vec3Tuple;
  /** World position of the end joint (hand / foot). */
  end: Vec3Tuple;
  /** World position the end joint should reach. */
  target: Vec3Tuple;
  /** Optional world-space pole: the mid joint is placed on this side of the root→target line. */
  pole?: Vec3Tuple;
  /** World-space bend direction, used when no pole is supplied. */
  bendDir: Vec3Tuple;
}

export interface TwoBoneIKResult {
  /** New world position of the middle joint. */
  mid: Vec3Tuple;
  /** New world position of the end joint. */
  end: Vec3Tuple;
  /** False when the target was outside `[ |l1-l2| , l1+l2 ]` and the chain had to be clamped. */
  reached: boolean;
}

export interface SolveChainOnBonesArgs {
  rootBone: Object3D;
  midBone: Object3D;
  endBone: Object3D;
  targetWorld: Vector3;
  poleWorld?: Vector3;
  bendDirWorld: Vector3;
}

// ---------------------------------------------------------------------------
// Tuple vector helpers (local; the shared layer only owns quaternion helpers)
// ---------------------------------------------------------------------------

function sub(a: Vec3Tuple, b: Vec3Tuple): Vec3Tuple {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function addScaled(a: Vec3Tuple, b: Vec3Tuple, s: number): Vec3Tuple {
  return [a[0] + b[0] * s, a[1] + b[1] * s, a[2] + b[2] * s];
}

function dot(a: Vec3Tuple, b: Vec3Tuple): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function cross(a: Vec3Tuple, b: Vec3Tuple): Vec3Tuple {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function len(a: Vec3Tuple): number {
  return Math.hypot(a[0], a[1], a[2]);
}

function scaled(a: Vec3Tuple, s: number): Vec3Tuple {
  return [a[0] * s, a[1] * s, a[2] * s];
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Component of `v` perpendicular to the unit vector `axis`. */
function perpComponent(v: Vec3Tuple, axis: Vec3Tuple): Vec3Tuple {
  return addScaled(v, axis, -dot(v, axis));
}

/**
 * Unit vector perpendicular to `axis` pointing to the bend side. Tries, in order:
 * the pole offset, the supplied bend direction, `bendDir × up`, then two world axes. The order
 * is fixed so the result is deterministic for identical input.
 */
function bendPerpendicular(
  root: Vec3Tuple,
  axis: Vec3Tuple,
  pole: Vec3Tuple | undefined,
  bendDir: Vec3Tuple,
): Vec3Tuple {
  const candidates: Vec3Tuple[] = [];
  if (pole) candidates.push(sub(pole, root));
  candidates.push(bendDir);
  candidates.push(cross(bendDir, WORLD_UP));
  candidates.push(cross(axis, WORLD_X));
  candidates.push(cross(axis, WORLD_Z));

  for (const candidate of candidates) {
    if (len(candidate) < EPS_PERP) continue;
    const perp = perpComponent(candidate, axis);
    const l = len(perp);
    if (l >= EPS_PERP) return scaled(perp, 1 / l);
  }
  // `axis` is a unit vector, so at least one of `axis × X` / `axis × Z` is non-degenerate;
  // this line is only reached if `axis` itself were degenerate, which callers prevent.
  return WORLD_UP;
}

// ---------------------------------------------------------------------------
// Pure solver
// ---------------------------------------------------------------------------

/**
 * Analytic two-bone solve. Segment lengths |root-mid| and |mid-end| are preserved exactly.
 *
 * Unreachable targets (farther than l1+l2, or closer than |l1-l2|) clamp: the chain extends
 * fully along root→target (or folds to the minimum reach) and `reached` is false. The cosine is
 * clamped to [-1, 1] before `acos`, so full extension produces a stable, NaN-free straight chain
 * instead of flickering.
 */
export function solveTwoBoneIK(input: TwoBoneIKInput): TwoBoneIKResult {
  const { root, mid, end, target, pole, bendDir } = input;

  const upper = sub(mid, root);
  const lower = sub(end, mid);
  const l1 = len(upper);
  const l2 = len(lower);

  const toTarget = sub(target, root);
  const dist = len(toTarget);

  // Direction root→target. Degenerate target (on the root) keeps the current limb direction.
  let axis: Vec3Tuple;
  if (dist > EPS_LEN) {
    axis = scaled(toTarget, 1 / dist);
  } else if (l1 > EPS_LEN) {
    axis = scaled(upper, 1 / l1);
  } else {
    axis = WORLD_UP;
  }

  const maxLen = l1 + l2;
  const minLen = Math.abs(l1 - l2);
  const reached = dist >= minLen - REACH_TOL && dist <= maxLen + REACH_TOL;

  // A zero-length bone has no triangle; lay both segments straight along the axis.
  if (l1 <= EPS_LEN || l2 <= EPS_LEN) {
    const newMid = addScaled(root, axis, l1);
    return { mid: newMid, end: addScaled(newMid, axis, l2), reached };
  }

  // Effective root→end distance after clamping to the chain's reach.
  const c = Math.max(clamp(dist, minLen, maxLen), EPS_LEN);

  // Law of cosines at the root joint.
  const cosA = clamp((l1 * l1 + c * c - l2 * l2) / (2 * l1 * c), -1, 1);
  const sinA = Math.sqrt(Math.max(0, 1 - cosA * cosA));

  const perp = bendPerpendicular(root, axis, pole, bendDir);

  const newMid: Vec3Tuple = [
    root[0] + axis[0] * (l1 * cosA) + perp[0] * (l1 * sinA),
    root[1] + axis[1] * (l1 * cosA) + perp[1] * (l1 * sinA),
    root[2] + axis[2] * (l1 * cosA) + perp[2] * (l1 * sinA),
  ];
  const newEnd = addScaled(root, axis, c);

  return { mid: newMid, end: newEnd, reached };
}

// ---------------------------------------------------------------------------
// Rest bend direction
// ---------------------------------------------------------------------------

/**
 * Bend direction implied by a rest pose: the elbow/knee offset from the root→end line,
 * normalized. Falls back to `chain.fallbackBendDir` when the rest limb is perfectly straight
 * (elbow exactly on the line), has zero length, or when any of the three joints is missing.
 *
 * The map is `Partial` on purpose: `BoundRig.restJointWorldPositions()` only lists the bones a
 * binding actually resolved, so a rig missing a limb (or an unmapped Root) must be able to call
 * this without a cast. Every read below is already guarded.
 */
export function restBendDirForChain(
  chain: IkChain,
  restJointWorldPositions: Partial<Record<CanonicalBoneId, Vec3Tuple>>,
): Vec3Tuple {
  const fallback = chain.fallbackBendDir;
  const root = restJointWorldPositions[chain.root];
  const mid = restJointWorldPositions[chain.mid];
  const end = restJointWorldPositions[chain.end];
  if (!root || !mid || !end) return [fallback[0], fallback[1], fallback[2]];

  const line = sub(end, root);
  const lineLen = len(line);
  if (lineLen <= EPS_LEN) return [fallback[0], fallback[1], fallback[2]];

  const axis = scaled(line, 1 / lineLen);
  const perp = perpComponent(sub(mid, root), axis);
  const perpLen = len(perp);
  if (perpLen < EPS_PERP) return [fallback[0], fallback[1], fallback[2]];

  return scaled(perp, 1 / perpLen);
}

// ---------------------------------------------------------------------------
// Bone layer
// ---------------------------------------------------------------------------

const _v1 = new Vector3();
const _v2 = new Vector3();
const _q1 = new Quaternion();
const _q2 = new Quaternion();
const _q3 = new Quaternion();

function worldPositionTuple(object: Object3D): Vec3Tuple {
  object.updateWorldMatrix(true, false);
  const e = object.matrixWorld.elements;
  return [e[12], e[13], e[14]];
}

/**
 * Rotate `bone` (about its own world position) so that `currentChild` lands on `desiredChild`.
 * Only `bone.quaternion` is written; the minimal from-to rotation is composed in world space and
 * converted back with `parentWorldQuat^-1 * newWorldQuat`.
 */
function aimBoneAt(bone: Object3D, pivot: Vec3Tuple, currentChild: Vec3Tuple, desiredChild: Vec3Tuple): void {
  _v1.set(currentChild[0] - pivot[0], currentChild[1] - pivot[1], currentChild[2] - pivot[2]);
  _v2.set(desiredChild[0] - pivot[0], desiredChild[1] - pivot[1], desiredChild[2] - pivot[2]);
  if (_v1.lengthSq() <= EPS_LEN || _v2.lengthSq() <= EPS_LEN) return;
  _v1.normalize();
  _v2.normalize();

  const delta = _q1.setFromUnitVectors(_v1, _v2);
  const currentWorld = bone.getWorldQuaternion(_q2);
  const newWorld = delta.multiply(currentWorld); // delta applied after the current orientation

  let local: Quaternion;
  if (bone.parent) {
    const parentWorld = bone.parent.getWorldQuaternion(_q3);
    local = parentWorld.invert().multiply(newWorld);
  } else {
    local = newWorld;
  }
  local.normalize();

  // Keep the same hemisphere as the current rotation. `Matrix4.decompose` may hand back the
  // double-cover twin (-q) of what we wrote, which is the same rotation but would make repeated
  // solves flip sign and look like a change to anything diffing raw quaternions.
  const q = bone.quaternion;
  if (q.x * local.x + q.y * local.y + q.z * local.z + q.w * local.w < 0) {
    local.set(-local.x, -local.y, -local.z, -local.w);
  }
  q.copy(local);
}

/**
 * Solve the chain on live bones. Reads world joint positions, runs `solveTwoBoneIK`, then aims
 * the root bone at the solved elbow/knee and the mid bone at the solved hand/foot.
 *
 * Never writes `position` or `scale`, and never touches `endBone.quaternion` (D22).
 */
export function solveChainOnBones(args: SolveChainOnBonesArgs): { reached: boolean } {
  const { rootBone, midBone, endBone, targetWorld, poleWorld, bendDirWorld } = args;

  const rootPos = worldPositionTuple(rootBone);
  const midPos = worldPositionTuple(midBone);
  const endPos = worldPositionTuple(endBone);

  const solved = solveTwoBoneIK({
    root: rootPos,
    mid: midPos,
    end: endPos,
    target: [targetWorld.x, targetWorld.y, targetWorld.z],
    pole: poleWorld ? [poleWorld.x, poleWorld.y, poleWorld.z] : undefined,
    bendDir: [bendDirWorld.x, bendDirWorld.y, bendDirWorld.z],
  });

  aimBoneAt(rootBone, rootPos, midPos, solved.mid);
  rootBone.updateWorldMatrix(false, true);

  // Re-read after the root rotation; the mid joint has moved with it.
  const midPos2 = worldPositionTuple(midBone);
  const endPos2 = worldPositionTuple(endBone);
  aimBoneAt(midBone, midPos2, endPos2, solved.end);
  midBone.updateWorldMatrix(false, true);

  return { reached: solved.reached };
}
